<template>
  <b-container>
    <div class="shipping-rule-configurator">
      <div class="shipping-rule-configurator__item-wrap">
        <span class="shipping-rule-configurator__text">{{ $t("if") }}</span>
        <b-form-select
          v-model="selectedConditionType"
          :options="conditionTypes"
        ></b-form-select>
      </div>
      <div class="shipping-rule-configurator__item-wrap">
        <span class="shipping-rule-configurator__text">{{
          comparisonOperators[selectedConditionType]
        }}</span>
        <b-form-select
          v-model="selectedCondition"
          :options="conditions[selectedConditionType]"
        ></b-form-select>
      </div>
      <div class="shipping-rule-configurator__item-wrap">
        <span class="shipping-rule-configurator__text">{{ $t("then") }}</span>
        <b-form-select
          v-model="selectedActionType"
          :options="actionTypes"
        ></b-form-select>
      </div>
      <b-form-select
        v-model="selectedAction"
        :options="actions[selectedActionType]"
        class="custom-select__last"
      ></b-form-select>
    </div>

    <div class="order-wrap">
      <div class="order-header">
        <div class="order-header-item order-header-item__customer">
          {{ $t("customer") }}
        </div>
        <div class="order-header-item">{{ $t("carrier") }}</div>
        <div class="order-header-item">{{ $t("weight") }}</div>
        <div class="order-header-item">{{ $t("value") }}</div>
        <div
          class="order-header-item order-header-item__dropshipping-address"
          v-if="selectedActionType === 'ship-with-address'"
        >
          {{ $t("ship from") }}
        </div>
        <div
          class="order-header-item"
          v-if="selectedActionType === 'insure-for'"
        >
          {{ $t("insurance") }}
        </div>
      </div>
      <div class="order-item-wrap">
        <transition-group name="fade" mode="out-in">
          <div class="order-item" v-for="order in orders" :key="order.customer">
            <div class="order-item-column order-item-column__customer">
              <span class="order-item__text">{{ order.customer }}</span>
              <small
                class="order-item__small-text text-muted"
                :class="{
                  'order-item__text--active':
                    selectedCondition === order.destination.code,
                }"
              >
                <img
                  class="order-item__country-logo"
                  :src="
                    `https://www.flagcdn.com/w20/${order.destination.code}.png`
                  "
                />
                {{ order.destination.name }}
              </small>
            </div>
            <div class="order-item-column">
              <span
                class="order-item__text"
                :class="{
                  'order-item__text--active':
                    selectedActionType === 'ship-with-carrier' &&
                    checkIfOrderMatchesCondition(order),
                }"
                >{{
                  checkIfOrderMatchesCondition(order) &&
                  selectedActionType === "ship-with-carrier"
                    ? selectedAction
                    : order.carrier.text
                }}</span
              >
              <small class="order-item__small-text text-muted">{{
                order.carrier.method
              }}</small>
            </div>
            <div class="order-item-column">
              <span
                class="order-item__text"
                :class="{
                  'order-item__text--active':
                    selectedConditionType === 'weight' &&
                    checkIfOrderMatchesCondition(order),
                }"
                >{{ order.weight }} kg</span
              >
            </div>
            <div class="order-item-column">
              <span
                class="order-item__text"
                :class="{
                  'order-item__text--active':
                    selectedConditionType === 'order-value' &&
                    checkIfOrderMatchesCondition(order),
                }"
                >{{ $t("€") }}{{ order.value }}</span
              >
            </div>
            <div
              class="order-item-column order-item-column__dropshipping-address"
              v-if="selectedActionType === 'ship-with-address'"
            >
              <span
                class="order-item__text order-item__text--active"
                v-if="checkIfOrderMatchesCondition(order)"
                >{{ `${selectedAction}` }}</span
              >
              <span class="order-item__text" v-else>–</span>
            </div>
            <div
              class="order-item-column"
              v-if="selectedActionType === 'insure-for'"
            >
              <span
                class="order-item__text order-item__text--active"
                v-if="checkIfOrderMatchesCondition(order)"
                >{{ `${$t("€")}${selectedAction}` }}</span
              >
              <span class="order-item__text" v-else>–</span>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
  </b-container>
</template>

<script>
require("chance");
import { BContainer, BFormSelect } from "bootstrap-vue";
import { getCurrentLanguage } from "@/lang/i18n.js";

import { CARRIERS } from "@/constants/carriers.js";

const COUNTRIES = [
  {
    name: "Belgium",
    code: "be",
  },
  {
    name: "Germany",
    code: "de",
  },
  {
    name: "The Netherlands",
    code: "nl",
  },
  {
    name: "United States",
    code: "us",
  },
  {
    name: "United Kingdom",
    code: "gb",
  },
  {
    name: "Canada",
    code: "ca",
  },
  {
    name: "France",
    code: "fr",
  },
  {
    name: "Sweden",
    code: "se",
  },
  {
    name: "Italy",
    code: "it",
  },
  {
    name: "Spain",
    code: "es",
  },
  {
    name: "Russia",
    code: "ru",
  },
  {
    name: "India",
    code: "in",
  },
];

export default {
  name: "ShippingRules",
  components: {
    BContainer,
    BFormSelect,
  },
  data() {
    const carriersForLanguage =
      CARRIERS[getCurrentLanguage()] || CARRIERS["en"];

    return {
      orders: [],
      selectedConditionType: "order-value",
      selectedCondition: 100,
      conditionTypes: [
        { value: "destination-country", text: this.$t("destination country") },
        { value: "weight", text: this.$t("weight") },
        { value: "order-value", text: this.$t("order value") },
      ],
      conditions: {
        "destination-country": [
          { value: "uk", text: this.$t("uk") },
          { value: "nl", text: this.$t("nl") },
          { value: "de", text: this.$t("de") },
          { value: "us", text: this.$t("us") },
          { value: "fr", text: this.$t("fr") },
        ],
        weight: [
          { value: 5, text: "5kg" },
          { value: 10, text: "10kg" },
          { value: 50, text: "50kg" },
          { value: 100, text: "100kg" },
        ],
        "order-value": [
          { value: 100, text: this.$t("€100") },
          { value: 200, text: this.$t("€200") },
          { value: 500, text: this.$t("€500") },
          { value: 1000, text: this.$t("€1000") },
        ],
      },
      selectedActionType: "insure-for",
      selectedAction: 200,
      actionTypes: [
        { value: "ship-with-carrier", text: this.$t("ship with carrier") },
        { value: "insure-for", text: this.$t("insure by") },
        { value: "ship-with-address", text: this.$t("ship with address") },
      ],
      actions: {
        "ship-with-carrier": carriersForLanguage.map((carrier) => {
          return { value: carrier.value, text: carrier.text };
        }),
        "insure-for": [
          { value: 100, text: this.$t("€100") },
          { value: 200, text: this.$t("€200") },
          { value: 500, text: this.$t("€500") },
          { value: 1000, text: this.$t("€1000") },
        ],
        "ship-with-address": [
          { value: this.$t("ship address 1"), text: this.$t("ship address 1") },
          { value: this.$t("ship address 2"), text: this.$t("ship address 2") },
          { value: this.$t("ship address 3"), text: this.$t("ship address 3") },
        ],
      },
      comparisonOperators: {
        "destination-country": this.$t("is"),
        weight: this.$t("is more than"),
        "order-value": this.$t("is more than"),
      },
      matchCount: 0,
    };
  },
  watch: {
    selectedActionType(newVal) {
      this.selectedAction = this.actions[newVal][0].value;
    },
    selectedConditionType(newVal) {
      this.selectedCondition = this.conditions[newVal][0].value;
    },
  },
  mounted() {
    this.createNewOrder();
    setInterval(this.createNewOrder, 3250);
  },
  methods: {
    checkIfOrderMatchesCondition(order) {
      const { weight, value } = order;
      const country = order.destination.code;

      return (
        (country === this.selectedCondition &&
          this.selectedConditionType === "destination-country") ||
        (weight > this.selectedCondition &&
          this.selectedConditionType === "weight") ||
        (value > this.selectedCondition &&
          this.selectedConditionType === "order-value")
      );
    },
    generateRandomOrder() {
      const carriersForLanguage =
        CARRIERS[getCurrentLanguage()] || CARRIERS["en"];

      return {
        customer: chance.name(),
        destination: chance.pickset(COUNTRIES)[0],
        carrier: chance.pickset(carriersForLanguage)[0],
        weight: chance.floating({ fixed: 1, min: 2, max: 120 }),
        value: chance.floating({ fixed: 2, min: 5, max: 500 }),
      };
    },
    createNewOrder() {
      this.orders.unshift(this.generateRandomOrder());

      if (this.orders.length > 3) {
        this.removeLastOrder();
      }
    },
    removeLastOrder() {
      this.orders.pop();
    },
  },
};
</script>

<style scoped lang="postcss">
.shipping-rule-configurator {
  display: flex;
  align-items: center;
}

@media screen and (max-width: 992px) {
  .shipping-rule-configurator {
    flex-wrap: wrap;
  }
}

.shipping-rule-configurator__text {
  color: #717b90;
  flex-shrink: 0;
  margin-right: 12px;
  margin-left: 12px;
}

@media screen and (max-width: 992px) {
  .shipping-rule-configurator__text {
    margin-left: 0;
  }
}

.custom-select {
  border-radius: 25px;
  font-family: "BeatriceMedium";
  color: var(--full-blue);
}

.custom-select__last {
  margin-left: 12px;
}
@media screen and (max-width: 992px) {
  .custom-select__last {
    margin-left: 0;
  }
}

.shipping-rule-configurator__item-wrap {
  width: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

@media screen and (max-width: 992px) {
  .shipping-rule-configurator__item-wrap {
    margin-bottom: 10px;
    width: 100%;
  }
}

.order-header {
  display: flex;
}
.order-header-item {
  width: 18.75%;
  padding: 10px 15px;
  color: var(--full-blue);
  font-family: "BeatriceMedium";
}

@media screen and (max-width: 768px) {
  .order-header-item {
    padding: 7.5px 7.5px;
  }
}

.order-header-item__customer,
.order-header-item__dropshipping-address {
  width: 25%;
}

.order-wrap {
  position: relative;
  width: 75%;
  margin: 0 auto;
  margin-top: 60px;
}

@media screen and (max-width: 992px) {
  .order-wrap {
    width: 100%;
  }
}
@media screen and (max-width: 768px) {
  .order-wrap {
    width: 100%;
    margin-top: 30px;
    font-size: 0.9em;
  }
}

.order-item-wrap {
  overflow: hidden;
  width: 100%;
  padding: 0px;
}
/* Order Item */
.order-item {
  width: 100%;
  display: flex;
  border: 1px solid #eaeaea;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 0px 0px;
  margin: 10px 0;
  background: white;
  transition: transform 0.8s, opacity 0.2s;
}

.order-item-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 18.75%;
  padding: 10px 15px;
}

@media screen and (max-width: 768px) {
  .order-item-column {
    padding: 7.5px 7.5px;
  }
}

.order-item-column__customer,
.order-item-column__dropshipping-address {
  width: 25%;
}

.order-item__small-text,
.order-item__text {
  max-width: 100%;
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.order-item__small-text {
  margin-top: 5px;
}

.order-item__text--active {
  background: #90ccff;
  border: 3px solid #1d97ff;
  border-radius: 25px;
  padding: 0 8px;
}

@media screen and (max-width: 576px) {
  .order-item__text--active {
    padding: 0 4px;
    margin-top: -4px;
  }
}

.fade-enter, .fade-leave-to
/* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: translateY(-60px);
}

.fade-leave-active {
  position: absolute;
}
</style>
